import React, { useState } from 'react';
import { useData } from '../context/DataContext';

const ConfigPage = () => {
  const { targets, addTarget, updateTarget, deleteTarget } = useData();
  const [showForm, setShowForm] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'daily',
    amount: '',
    frequency: '',
    unit: '',
    reward: '',
    punishment: ''
  });

  const targetTypes = [
    { value: 'daily', label: 'Daily (e.g., 50 pushups every day)' },
    { value: 'weekly_frequency', label: 'Weekly Frequency (e.g., 3 sessions per week)' },
    { value: 'weekly_total', label: 'Weekly Total (e.g., 100 squats total)' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.amount) {
      alert('Please fill in all required fields');
      return;
    }

    const targetData = {
      ...formData,
      amount: parseInt(formData.amount),
      frequency: formData.frequency ? parseInt(formData.frequency) : null
    };

    if (editingTarget) {
      updateTarget(editingTarget.id, targetData);
      setEditingTarget(null);
    } else {
      addTarget(targetData);
    }

    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'daily',
      amount: '',
      frequency: '',
      unit: '',
      reward: '',
      punishment: ''
    });
    setShowForm(false);
    setEditingTarget(null);
  };

  const handleEdit = (target) => {
    setFormData({
      name: target.name,
      type: target.type,
      amount: target.amount.toString(),
      frequency: target.frequency?.toString() || '',
      unit: target.unit || '',
      reward: target.reward || '',
      punishment: target.punishment || ''
    });
    setEditingTarget(target);
    setShowForm(true);
  };

  const handleDelete = (targetId) => {
    if (window.confirm('Are you sure you want to delete this target?')) {
      deleteTarget(targetId);
    }
  };

  const getTargetDescription = (target) => {
    switch (target.type) {
      case 'daily':
        return `${target.amount} ${target.unit} every day`;
      case 'weekly_frequency':
        return `${target.amount} ${target.unit} on at least ${target.frequency} days per week`;
      case 'weekly_total':
        return `${target.amount} ${target.unit} total per week`;
      default:
        return '';
    }
  };

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 className="card-title">Weekly Fitness Targets</h2>
          <button 
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
          >
            + Add Target
          </button>
        </div>

        {targets.length === 0 ? (
          <div className="empty-state">
            <h3>No targets configured yet</h3>
            <p>Add your first fitness target to get started!</p>
          </div>
        ) : (
          <div className="grid">
            {targets.map(target => (
              <div key={target.id} className="card" style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ marginBottom: '0.5rem', color: '#2d3748' }}>{target.name}</h3>
                    <p style={{ color: '#718096', marginBottom: '1rem' }}>
                      {getTargetDescription(target)}
                    </p>
                    
                    {target.reward && (
                      <div style={{ marginBottom: '0.5rem' }}>
                        <span className="badge badge-success">🎁 Reward: {target.reward}</span>
                      </div>
                    )}
                    
                    {target.punishment && (
                      <div>
                        <span className="badge badge-danger">⚠️ Punishment: {target.punishment}</span>
                      </div>
                    )}
                  </div>
                  
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button 
                      className="btn btn-secondary btn-small"
                      onClick={() => handleEdit(target)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-danger btn-small"
                      onClick={() => handleDelete(target.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <div className="card">
          <h3 className="card-title">
            {editingTarget ? 'Edit Target' : 'Add New Target'}
          </h3>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Target Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="form-input"
                placeholder="e.g., Daily Pushups, Couch to 5K"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Target Type *</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className="form-select"
                required
              >
                {targetTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Amount *</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="e.g., 50, 100"
                  min="1"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Unit</label>
                <input
                  type="text"
                  name="unit"
                  value={formData.unit}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="e.g., pushups, squats, sessions"
                />
              </div>
            </div>

            {formData.type === 'weekly_frequency' && (
              <div className="form-group">
                <label className="form-label">Minimum Days per Week</label>
                <input
                  type="number"
                  name="frequency"
                  value={formData.frequency}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="e.g., 3, 4, 5"
                  min="1"
                  max="7"
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Reward (if completed)</label>
              <input
                type="text"
                name="reward"
                value={formData.reward}
                onChange={handleInputChange}
                className="form-input"
                placeholder="e.g., Buy new workout gear, Movie night"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Punishment (if not completed)</label>
              <input
                type="text"
                name="punishment"
                value={formData.punishment}
                onChange={handleInputChange}
                className="form-input"
                placeholder="e.g., Extra 30 minutes workout, No dessert this week"
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button type="submit" className="btn btn-primary">
                {editingTarget ? 'Update Target' : 'Add Target'}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={resetForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ConfigPage;
